import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useLocalization } from '@fluent/react';

export type ModuleOption = {
    id: number;
    name: string;
    description?: string | null;
};

export interface ModuleSelectProps {
    modules: ModuleOption[];
    value?: number | null;
    onChange: (value: number | null, option: ModuleOption | null) => void;
    label?: string;
    placeholder?: string;
    fullWidth?: boolean;
    size?: 'small' | 'medium';
    disabled?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    /** Exclude system-level module (-1) from options */
    excludeSystemLevel?: boolean;
    sx?: any;
}

export const ModuleSelect: React.FC<ModuleSelectProps> = ({
    modules,
    value,
    onChange,
    label,
    placeholder,
    fullWidth = true,
    size = 'medium',
    disabled,
    required,
    error,
    helperText,
    excludeSystemLevel = true,
    sx,
}) => {
    const { l10n } = useLocalization();

    const options = React.useMemo(() => {
        if (excludeSystemLevel) {
            return modules.filter(m => m.id !== -1);
        }
        return modules;
    }, [modules, excludeSystemLevel]);

    const selected = React.useMemo(
        () => options.find(o => o.id === value) || null,
        [options, value]
    );

    const defaultLabel = l10n.getString("cs-select-module", null, "_Wybierz moduł");
    const defaultPlaceholder = l10n.getString("cs-search-module", null, "_Wyszukaj moduł...");

    return (
        <Autocomplete<ModuleOption, false, false, false>
            options={options}
            value={selected}
            onChange={(_, opt) => onChange(opt?.id ?? null, opt ?? null)}
            getOptionLabel={(opt) => opt.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disabled={disabled}
            sx={sx}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                    <div>
                        <div style={{ fontWeight: 500 }}>{option.name}</div>
                        {option.description && (
                            <div style={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                {option.description}
                            </div>
                        )}
                    </div>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label || defaultLabel}
                    placeholder={placeholder || defaultPlaceholder}
                    fullWidth={fullWidth}
                    size={size}
                    disabled={disabled}
                    required={required}
                    error={error}
                    helperText={helperText}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <InputAdornment position="start">
                                <AccountTreeIcon fontSize="small" color="primary" />
                            </InputAdornment>
                        ),
                    }}
                />
            )}
        />
    );
};
